import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { UseRestrictionSelect } from '@/components/ui/use-restriction-select';
import { useDomains, useUpdateDomains } from '@/hooks/use-domains';

interface UseRestrictionModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  dealDomains: string[];
}

interface DomainFormState {
  name: string;
  use_restriction: string;
  use_restriction_note: string;
  selected: boolean;
  modified: boolean;
}

export function UseRestrictionModal({ open, onOpenChange, dealDomains }: UseRestrictionModalProps) {
  const { data: allDomains, isLoading } = useDomains();
  const updateDomains = useUpdateDomains();

  // State for domain data with form values
  const [domainFormData, setDomainFormData] = useState<DomainFormState[]>([]);
  const [selectAll, setSelectAll] = useState(false);

  // Initialize form data when domains are loaded
  useEffect(() => {
    if (open && allDomains) {
      const filteredDomains = allDomains.filter((d) => dealDomains.includes(d.name));

      // Create form state from domains
      const initialFormData = filteredDomains.map((domain) => ({
        name: domain.name,
        use_restriction: domain.use_restriction || '',
        use_restriction_note: domain.use_restriction_note || '',
        selected: false,
        modified: false,
      }));

      setDomainFormData(initialFormData);
      setSelectAll(false);
    }
  }, [open, allDomains, dealDomains]);

  // Handle select all domains
  const handleSelectAll = (checked: boolean) => {
    setSelectAll(checked);
    setDomainFormData((prev) =>
      prev.map((item) => ({
        ...item,
        selected: checked,
      })),
    );
  };

  // Handle single domain selection
  const handleSelectDomain = (domainName: string, checked: boolean) => {
    setDomainFormData((prev) =>
      prev.map((item) =>
        item.name === domainName
          ? { ...item, selected: checked }
          : item,
      ),
    );

    // Update selectAll state based on if all items are selected or not
    const updatedSelection = domainFormData.map((d) =>
      d.name === domainName ? checked : d.selected,
    );

    const areAllSelected = updatedSelection.every(Boolean);
    setSelectAll(areAllSelected);
  };

  // Handle field changes - updates all selected domains if the changed domain is selected
  const handleFieldChange = (
    domainName: string,
    field: 'use_restriction' | 'use_restriction_note',
    value: string,
  ) => {
    const domain = domainFormData.find((d) => d.name === domainName);

    // If the domain being edited is selected, update all selected domains
    if (domain?.selected) {
      setDomainFormData((prev) =>
        prev.map((item) =>
          item.selected
            ? {
                ...item,
                [field]: value,
                modified: true,
              }
            : item,
        ),
      );
    } else {
      // If the domain being edited is not selected, update only that domain
      setDomainFormData((prev) =>
        prev.map((item) =>
          item.name === domainName
            ? {
                ...item,
                [field]: value,
                modified: true,
              }
            : item,
        ),
      );
    }
  };

  // Save changes to all modified domains
  const handleSave = async () => {
    // Get all modified domains
    const modifiedDomains = domainFormData.filter((item) => item.modified);

    if (modifiedDomains.length === 0) {
      toast.error('No changes to save');
      return;
    }

    try {
      // Create promises for each domain update
      const updatePromises = modifiedDomains.map(async (domain) => {
        const updateData = {
          use_restriction: domain.use_restriction.trim() || null,
          use_restriction_note: domain.use_restriction_note.trim() || null,
        };

        return updateDomains.mutateAsync({
          domains: [domain.name],
          updateData,
        });
      });

      // Execute all updates
      await Promise.all(updatePromises);
      toast.success(`Updated ${modifiedDomains.length} domain${modifiedDomains.length > 1 ? 's' : ''}`);
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to update some domains');
      console.error(error);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Edit Use Restriction</DialogTitle>
          <DialogDescription>
            Edit use restriction fields directly or select multiple domains to edit them together.
          </DialogDescription>
        </DialogHeader>

        <div>
          <div className="mb-4 flex items-center">
            <Checkbox
              id="select-all"
              checked={selectAll}
              onCheckedChange={handleSelectAll}
            />
            <Label htmlFor="select-all" className="ml-2">
              Select all domains
            </Label>
            <span className="ml-4 text-sm text-muted-foreground">
              {domainFormData.filter((d) => d.selected).length}
              {' '}
              selected
            </span>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[50px]"></TableHead>
                <TableHead className="w-[180px]">Domain</TableHead>
                <TableHead className="w-[280px]">Use Restriction</TableHead>
                <TableHead className="w-[280px]">Use Restriction Note</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading
                ? (
                    <TableRow>
                      <TableCell colSpan={4} className="py-8 text-center">
                        Loading domain information...
                      </TableCell>
                    </TableRow>
                  )
                : ((domainFormData.length === 0)
                    ? (
                        <TableRow>
                          <TableCell colSpan={4} className="py-8 text-center">
                            No domains available in this deal
                          </TableCell>
                        </TableRow>
                      )
                    : (
                        domainFormData.map((domain) => (
                          <TableRow key={domain.name}>
                            <TableCell>
                              <Checkbox
                                checked={domain.selected}
                                onCheckedChange={(checked) =>
                                  handleSelectDomain(domain.name, !!checked)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">{domain.name}</TableCell>
                            <TableCell>
                              <UseRestrictionSelect
                                value={domain.use_restriction}
                                onChange={(value) =>
                                  handleFieldChange(domain.name, 'use_restriction', value)}
                                placeholder="Not set"
                              />
                            </TableCell>
                            <TableCell>
                              <Input
                                value={domain.use_restriction_note}
                                onChange={(e) =>
                                  handleFieldChange(domain.name, 'use_restriction_note', e.target.value)}
                                placeholder="Not set"
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      ))}
            </TableBody>
          </Table>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!domainFormData.some((d) => d.modified) || updateDomains.isPending}
          >
            {updateDomains.isPending ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
